import { useEffect, useState } from "react";
import api, { formatInr, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const EMPTY = {
  id: null, name: "", slug: "", brand: "", category: "",
  short_description: "", description: "", price: 0, mrp: 0,
  image: "", featured: false, in_stock: true,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    const payload = { ...form, price: Number(form.price), mrp: form.mrp ? Number(form.mrp) : null, specs: form.specs || {}, images: form.images || [] };
    try {
      if (form.id) await api.put(`/admin/products/${form.id}`, payload);
      else await api.post(`/admin/products`, payload);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="overline">Catalogue</div>
          <h1 className="font-display text-4xl mt-2">Products</h1>
        </div>
        <button onClick={() => setEditing(EMPTY)} data-testid="admin-new-product" className="fm-btn-primary inline-flex items-center gap-2"><Plus size={16} /> New product</button>
      </div>

      <div className="bg-white border border-[var(--fm-border)] overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[var(--fm-sage)] text-left">
            <tr>
              <Th>Name</Th><Th>Brand</Th><Th>Category</Th><Th>Price</Th><Th>Featured</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[var(--fm-border)]">
                <Td><div className="font-medium">{p.name}</div><div className="text-xs text-[var(--fm-text-3)]">{p.slug}</div></Td>
                <Td>{p.brand}</Td>
                <Td>{p.category}</Td>
                <Td className="font-mono-fm">{formatInr(p.price)}</Td>
                <Td>{p.featured ? "Yes" : "—"}</Td>
                <Td className="text-right">
                  <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.id}`} className="p-2 text-[var(--fm-text-2)] hover:text-[var(--fm-primary)]"><Pencil size={14} /></button>
                  <button onClick={() => del(p.id)} className="p-2 text-[var(--fm-text-2)] hover:text-red-600"><Trash2 size={14} /></button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <ProductDialog initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 overline">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function ProductDialog({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full p-8 border border-[var(--fm-border)] my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <div>
            <div className="overline text-[var(--fm-accent)]">{form.id ? "Edit" : "New"}</div>
            <div className="font-display text-2xl">Product details</div>
          </div>
          <button onClick={onClose}><X /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <In label="Name *" v={form.name} on={(v) => setForm({ ...form, name: v })} />
          <In label="Slug *" v={form.slug} on={(v) => setForm({ ...form, slug: v })} />
          <In label="Brand *" v={form.brand} on={(v) => setForm({ ...form, brand: v })} />
          <In label="Category *" v={form.category} on={(v) => setForm({ ...form, category: v })} />
          <In label="Price (₹) *" type="number" v={form.price} on={(v) => setForm({ ...form, price: v })} />
          <In label="MRP (₹)" type="number" v={form.mrp} on={(v) => setForm({ ...form, mrp: v })} />
          <In label="Image URL *" v={form.image} on={(v) => setForm({ ...form, image: v })} className="sm:col-span-2" />
          <In label="Short description" v={form.short_description} on={(v) => setForm({ ...form, short_description: v })} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <label className="overline">Long description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.in_stock} onChange={(e) => setForm({ ...form, in_stock: e.target.checked })} /> In stock</label>
        </div>

        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="fm-btn-secondary flex-1">Cancel</button>
          <button onClick={() => onSave(form)} data-testid="save-product" className="fm-btn-primary flex-1">Save</button>
        </div>
      </div>
    </div>
  );
}

function In({ label, v, on, type = "text", className = "" }) {
  return (
    <div className={className}>
      <label className="overline">{label}</label>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} className="w-full border border-[var(--fm-border)] mt-2 px-3 py-2.5 text-sm bg-white" />
    </div>
  );
}
